import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = 'admin' | 'moderator' | 'user';

/**
 * Hook to fetch the current user's roles
 * @returns Array of roles assigned to the authenticated user
 */
export const useUserRoles = () => {
  return useQuery({
    queryKey: ['user-roles'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return [];
      }

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      if (error) {
        console.error('[useUserRoles] Error fetching roles:', error);
        throw error;
      }

      return (data || []).map(item => item.role as AppRole);
    },
    enabled: true,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

/**
 * Hook to check if the current user has a specific role
 * @param role - The role to check for
 * @returns Boolean indicating if user has the role
 */
export const useHasRole = (role: AppRole) => {
  const { data: roles = [], isLoading } = useUserRoles();
  return {
    hasRole: roles.includes(role),
    isLoading,
  };
};

/**
 * Hook to check if the current user is an admin
 * @returns Boolean indicating if user is an admin
 */
export const useIsAdmin = () => {
  const { hasRole, isLoading } = useHasRole('admin');
  return { isAdmin: hasRole, isLoading };
};

/**
 * Hook to check if the current user is a moderator or admin
 * @returns Boolean indicating if user has moderator privileges
 */
export const useIsModerator = () => {
  const { data: roles = [], isLoading } = useUserRoles();
  const isModerator = roles.includes('moderator') || roles.includes('admin');
  return { isModerator, isLoading };
};
